import { useTokenStore } from "@/stores/token.store";
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { axiosInstance } from "./axiosInstance";
import {
  MAX_RETRIES,
  NON_RE_TRY_ABLE_ERRORS,
  RE_TRY_ABLE_ERRORS,
  REFRESH_BUFFER_SECONDS,
} from "./constants";
import { ApiError, wrapError } from "./handleError";
import { isTokenExpired, performTokenRefresh } from "./tokenManager";
import { ErrorResponse, InternalCustomAxiosRequestConfig } from "./types";

// State for token refresh
let isRefreshing = false;
let refreshPromise: Promise<string | null | undefined> | null = null;
let pendingRequests: Array<(token: string) => void> = [];

// Store for AbortControllers
const abortControllers = new Set<AbortController>();

// Clear AbortControllers without aborting
export const clearAbortControllers = (): void => {
  abortControllers.clear();
};

// Refresh token logic adapted from apiWrapper
const refreshTokenIfNeeded = async (
  tokenStore: ReturnType<typeof useTokenStore>,
  bufferSeconds: number = REFRESH_BUFFER_SECONDS
): Promise<string | null | undefined> => {
  if (
    bufferSeconds >= 0 &&
    tokenStore.token &&
    !isTokenExpired(tokenStore.token, bufferSeconds)
  ) {
    return tokenStore.token;
  }

  if (!refreshPromise) {
    isRefreshing = true;
    refreshPromise = performTokenRefresh(tokenStore)
      .then((newToken) => {
        if (newToken) {
          pendingRequests.forEach((callback) => callback(newToken));
        }
        return newToken;
      })
      .catch((error) => {
        throw wrapError(error, "Token refresh failed", "REFRESH_FAILED");
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
        pendingRequests = [];
      });
  }

  return refreshPromise;
};

// Request Interceptor
axiosInstance.interceptors.request.use(
  async (
    config: InternalCustomAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> => {
    const tokenStore = useTokenStore();
    const accessLevel = config.accessLevel || "private";

    // Add AbortController if no signal is provided
    if (!config.signal) {
      const controller = new AbortController();
      abortControllers.add(controller);
      config.signal = controller.signal;
    }

    // Handle token for private APIs
    if (accessLevel === "private") {
      const token = await refreshTokenIfNeeded(tokenStore);
      if (!token) {
        throw wrapError(
          new Error("No valid token available"),
          "No valid token available",
          "NO_TOKEN"
        );
      }
      config.headers.set("Authorization", `Bearer ${token}`);
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    // Clean up AbortController on error
    if (error.config?.signal instanceof AbortSignal) {
      const controller = Array.from(abortControllers).find(
        (c) => c.signal === error?.config?.signal
      );
      if (controller) {
        abortControllers.delete(controller);
      }
    }
    return Promise.reject(
      wrapError(error, "Request setup failed", "REQUEST_SETUP_FAILED")
    );
  }
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Clean up AbortController on success
    if (response.config.signal instanceof AbortSignal) {
      const controller = Array.from(abortControllers).find(
        (c) => c.signal === response.config.signal
      );
      if (controller) {
        abortControllers.delete(controller);
      }
    }
    return response;
  },
  async (error: AxiosError): Promise<AxiosResponse | Promise<AxiosError>> => {
    const tokenStore = useTokenStore();
    const config = error.config as InternalCustomAxiosRequestConfig | undefined;

    // Clean up AbortController on error
    if (config?.signal instanceof AbortSignal) {
      const controller = Array.from(abortControllers).find(
        (c) => c.signal === config.signal
      );
      if (controller) {
        abortControllers.delete(controller);
      }
    }

    if (!config) {
      return Promise.reject(
        wrapError(error, "Invalid request configuration", "INVALID_CONFIG")
      );
    }

    // Handle 401 errors with token refresh
    if (
      error.response?.status === 401 &&
      config.accessLevel !== "public" &&
      !config._retryCount
    ) {
      if (isRefreshing) {
        // Queue the request until refresh completes
        return new Promise<AxiosResponse>((resolve, reject) => {
          pendingRequests.push((newToken: string) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            axiosInstance(config)
              .then(resolve)
              .catch((err) =>
                reject(wrapError(err, "Retry request failed", "RETRY_FAILED"))
              );
          });
        });
      }

      try {
        const newToken = await refreshTokenIfNeeded(tokenStore, -1); // Explicit refresh
        if (!newToken) {
          throw new ApiError({
            message: "No valid token after refresh",
            success: false,
            code: "NO_TOKEN",
          });
        }
        config.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(config);
      } catch (refreshError) {
        return Promise.reject(
          wrapError(refreshError, "Token refresh failed", "REFRESH_FAILED")
        );
      }
    }

    // Handle retryable errors
    const isRetryableError = RE_TRY_ABLE_ERRORS.includes(error?.code || "");

    const isNonRetryableError = NON_RE_TRY_ABLE_ERRORS.includes(
      error.response?.status.toString() || ""
    );

    const isNonRetryableCustomError = NON_RE_TRY_ABLE_ERRORS.includes(
      (error.response?.data as ErrorResponse)?.code || ""
    );

    const canRetry =
      isRetryableError && !isNonRetryableError && !isNonRetryableCustomError;

    const retryCount = config._retryCount || 0;

    if (canRetry && retryCount < MAX_RETRIES) {
      config._retryCount = retryCount + 1;
      console.warn(
        `Retry ${retryCount + 1}/${MAX_RETRIES} for error: ${error?.code}`
      );

      // Create new AbortController for retry if no signal was provided
      if (!config.signal) {
        const controller = new AbortController();
        abortControllers.add(controller);
        config.signal = controller.signal;
      }

      return axiosInstance(config);
    }

    // Handle abort/timeout errors
    const isAbortError =
      error?.code === "ERR_CANCELED" ||
      error?.code === "ECONNABORTED" ||
      error.message.includes("timeout") ||
      error.message.includes("aborted");

    if (isAbortError) {
      return Promise.reject(
        wrapError(error, "Request aborted", error?.code || "ABORT_ERROR")
      );
    }

    // Handle other errors
    return Promise.reject(
      wrapError(error, "Request failed", error?.code || "REQUEST_FAILED")
    );
  }
);

export { axiosInstance };
